import { handleResponseOld, makeGet, makePost } from './helpers';

/**
 * @category Telegram API
 * @see {fetchNotificationsParams}
 */
type FetchNotificationsParams = {
    authToken: string;
    deployment: string;
};

/**
 * fetches current notifications via Get request to Telegram API
 * @function fetchNotifications
 * @category Telegram API
 * @param {FetchNotificationsParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/getNotifications
 * @see https://github.com/LIVEauctioneers/telegram/blob/master/internal/handlers/notificationsHandler.go
 */
const fetchNotifications = ({ authToken, deployment }: FetchNotificationsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<NOTIFICATIONS-API>getNotifications' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Telegram API
 * @see {postDisableWebPushSubscription}
 */
type PostDisableWebPushSubscriptionParams = {
    authToken: string;
    deployment: string;
};
/**
 * sends disable web push subscription request via Post to Telegram API
 * @function postDisableWebPushSubscription
 * @category Telegram API
 * @param {PostDisableWebPushSubscriptionParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/disableWebPushSubscription
 * @see https://github.com/LIVEauctioneers/telegram/blob/master/internal/handlers/notificationPreferencesHandler.go
 */
const postDisableWebPushSubscription = ({ authToken, deployment }: PostDisableWebPushSubscriptionParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<NOTIFICATIONS-API>disableWebPushSubscription' });
        request.send();
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Telegram API
 * @see {postMarkDeleted}
 */
type PostMarkDeletedParams = {
    authToken: string;
    deployment: string;
    notificationIds: any;
};
/**
 * updates a notification as deleted via Post to Telegram API
 * @function postMarkDeleted
 * @category Telegram API
 * @param {PostMarkDeletedParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/markNotificationsDeleted
 * @see https://github.com/LIVEauctioneers/telegram/blob/master/internal/handlers/notificationsHandler.go
 */
const postMarkDeleted = ({ authToken, deployment, notificationIds }: PostMarkDeletedParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<NOTIFICATIONS-API>markNotificationsDeleted' });
        request.send({ ids: notificationIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Telegram API
 * @see {postMarkRead}
 */
type PostMarkReadParams = {
    authToken: string;
    deployment: string;
    notificationIds: any;
};
/**
 * updates a notification as read via Post to Telegram API
 * @function postMarkRead
 * @category Telegram API
 * @param {PostMarkReadParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/markNotificationsRead
 * @see https://github.com/LIVEauctioneers/telegram/blob/master/internal/handlers/notificationsHandler.go
 */
const postMarkRead = ({ authToken, deployment, notificationIds }: PostMarkReadParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<NOTIFICATIONS-API>markNotificationsRead' });
        request.send({ ids: notificationIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Telegram API
 * @see postUpdateWebPushSubscription
 */
type PostUpdateWebPushSubscriptionParams = {
    authToken: string;
    deployment: string;
    subscription: any;
};
/**
 * updates a web push subscription request via Post to Telegram API
 * @function postUpdateWebPushSubscription
 * @category Telegram API
 * @param {PostUpdateWebPushSubscriptionParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/updateWebPushSubscription
 * @see https://github.com/LIVEauctioneers/telegram/blob/master/internal/handlers/notificationPreferencesHandler.go
 */
const postUpdateWebPushSubscription = ({ authToken, deployment, subscription }: PostUpdateWebPushSubscriptionParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<NOTIFICATIONS-API>updateWebPushSubscription' });
        request.send(subscription);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    fetchNotifications,
    postDisableWebPushSubscription,
    postMarkDeleted,
    postMarkRead,
    postUpdateWebPushSubscription,
};
